import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { type Dispatch, type SetStateAction, createContext, useContext } from 'react';

import type { Language, Project, StyleGuide, StyleGuideEdit } from '@app/api/styleGuide';

import type { RejectedFile } from './model/rejectedFile';

type VoidResponse = { data: void } | { error: FetchBaseQueryError | SerializedError };

type StyleGuideContextValue = {
    languages: Language[];
    setLanguages: Dispatch<SetStateAction<Language[]>>;
    projects: Project[];
    setProjects: Dispatch<SetStateAction<Project[]>>;
    filesToUpload: File[] | null;
    filesRejectedForUpload: RejectedFile[] | null;
    uploadStyleGuideFiles: (files?: File[], rejectedFiles?: RejectedFile[]) => void;
    acknowledgeStyleGuideFiles: (files: File[], rejectedFiles?: RejectedFile[]) => void;
    onOpen: (styleGuideId: string) => void;
    onEdit: (styleGuide: StyleGuide) => void;
    onDelete: (styleGuide: StyleGuide) => void;
    saveStyleGuides: (updates: StyleGuideEdit[]) => Promise<VoidResponse>;
    teamId: number;
};

const StyleGuideContext = createContext<StyleGuideContextValue | undefined>(undefined);

const useStyleGuideContext = (): StyleGuideContextValue => {
    const context = useContext(StyleGuideContext);
    if (!context) {
        throw new Error('useStyleGuideContext must be used within a StyleGuideProvider');
    }

    return context;
};

export { useStyleGuideContext, StyleGuideContext, type StyleGuideContextValue };
